import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  IconButton,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import { useSelector } from "react-redux";
import { getUserDetails } from "../../Redux/Slice/Settings/UserSlice/UserSlice";

// ---------------------------------------------------------------------------
// Type-driven config helpers (mirrors ERP SubscriptionExpireModal pattern)
// ---------------------------------------------------------------------------
const getIconConfig = (type) => {
  switch (type) {
    case "blocked":
      return {
        Icon: BlockIcon,
        iconColor: "#b02a1f",
        bgColor: "#fbdad6",
      };
    case "inactive":
      return {
        Icon: WarningAmberIcon,
        iconColor: "#00897b",
        bgColor: "#d0ecebff",
      };
    case "expire":
    default:
      return {
        Icon: AccessTimeIcon,
        iconColor: "#00897b",
        bgColor: "#d0ecebff",
      };
  }
};

const getButtonLabel = (type) =>
  type === "blocked" ? "OK, Got It" : "Remind Me Later";

// ---------------------------------------------------------------------------
// PaymentReminderModal
// ---------------------------------------------------------------------------
export function PaymentReminderModal({ open, onClose, expireOn, type }) {
  const { Icon, iconColor, bgColor } = getIconConfig(type);

  // Compute "access denied after" date: expireOn + 7 days (mirrors ERP)
  const accessDeniedDate = expireOn
    ? (() => {
        const d = new Date(expireOn);
        d.setDate(d.getDate() + 7);
        return d.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      })()
    : null;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          backdropFilter: "blur(12px)",
          background: "linear-gradient(135deg, #ffffffee, #f8fbffdd)",
          boxShadow: "0 12px 50px rgba(0,0,0,0.25)",
        },
      }}
      sx={{ zIndex: 9999 }}
    >
      <DialogContent sx={{ p: 4, position: "relative" }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <HighlightOffIcon sx={{ fontSize: "26px" }} />
        </IconButton>

        {/* Dynamic icon */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              bgcolor: bgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: iconColor, fontSize: 28 }} />
          </Box>
        </Box>

        {/* Dynamic message */}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.75, textAlign: "center" }}
        >
          {type === "inactive" && (
            <>
              Your subscription is currently <strong>inactive</strong> due to a
              pending payment or access restriction. Please complete the payment
              or contact support to reactivate.
            </>
          )}

          {type === "blocked" && (
            <>
              Your subscription has expired and account access has been
              suspended.
              <br />
              Please contact your account manager or our support team to
              reactivate your subscription.
            </>
          )}

          {(type === "expire" || !type) && (
            <>
              Your subscription has expired.
              {accessDeniedDate && (
                <>
                  {" "}
                  To avoid losing access after <strong>{accessDeniedDate}</strong>,
                </>
              )}{" "}
              please renew your subscription at your earliest convenience.
            </>
          )}
        </Typography>

        {/* Dynamic dismiss button */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Box
            component="button"
            onClick={onClose}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              border: "1.5px solid",
              borderColor: "grey.400",
              bgcolor: "grey.100",
              color: "text.primary",
              fontWeight: 600,
              fontSize: "0.85rem",
              cursor: "pointer",
              transition: "background 0.2s",
              "&:hover": { bgcolor: "grey.200" },
            }}
          >
            {getButtonLabel(type)}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

PaymentReminderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  expireOn: PropTypes.string,
  type: PropTypes.oneOf(["inactive", "blocked", "expire"]),
};

PaymentReminderModal.defaultProps = {
  expireOn: null,
  type: "expire",
};

// ---------------------------------------------------------------------------
// PaymentReminderDemo  (wrapper consumed by App.js)
// ---------------------------------------------------------------------------
export default function PaymentReminderDemo() {
  const [dismissed, setDismissed] = useState(false);
  const { CurrentUser } = useSelector(getUserDetails);

  // Reset dismissed only when CurrentUser changes (e.g. after re-fetch / new login),
  // NOT on every route change — mirrors ERP where modalDismissed resets only
  // when subscriptionError changes.
  React.useEffect(() => {
    if (CurrentUser?.ExpireShow) {
      setDismissed(false);
    }
  }, [CurrentUser]);

  const shouldShow =
    !dismissed && (CurrentUser?.ExpireShow === true || CurrentUser?.ExpireShow === "true");

  const expireOn = CurrentUser?.ExpireOn ?? null;

  // Derive modal type from API field (mirrors ERP pattern).
  // Falls back to "expire" if ExpireType is absent.
  const type = CurrentUser?.ExpireType ?? "expire";

  // Compute access-denied date: expireOn + 7 days (same as ERP)
  const accessDeniedDate = expireOn
    ? (() => {
        const d = new Date(expireOn);
        d.setDate(d.getDate() + 7);
        return d.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })()
    : null;

  const showBanner =
    CurrentUser?.ExpireShow === true || CurrentUser?.ExpireShow === "true";

  return (
    <>
      <PaymentReminderModal
        open={shouldShow}
        onClose={() => setDismissed(true)}
        expireOn={expireOn}
        type={type}
      />

      {/* Scrolling ticker banner — fixed at the bottom of the screen.
          Stays visible the whole session while ExpireShow is true,
          independent of the modal being dismissed. */}
      {showBanner && (
        <>
          <style>{`
            @keyframes paymentTicker {
              0%   { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
            .payment-ticker-inner {
              display: inline-flex;
              align-items: center;
              white-space: nowrap;
              animation: paymentTicker 32s linear infinite;
            }
            .payment-ticker-sep {
              display: inline-block;
              margin: 0 28px;
              opacity: 0.5;
            }
          `}</style>
          <div
            role="alert"
            aria-live="polite"
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 99999,
              background: "linear-gradient(90deg, #b02a1f 0%, #d9483a 100%)",
              color: "#fff",
              padding: "6px 0",
              fontSize: "12.5px",
              fontWeight: 500,
              letterSpacing: "0.2px",
              fontFamily: "inherit",
              boxShadow: "0 -2px 8px rgba(0,0,0,0.25)",
              overflow: "hidden",
              borderTop: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            <span className="payment-ticker-inner">
              {[0, 1].map((i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                  <WarningAmberIcon sx={{ fontSize: 14, mr: "8px", flexShrink: 0 }} />
                  <span>
                    Your subscription has expired.
                    {accessDeniedDate && (
                      <>
                        {" "}
                        Access will be blocked after <strong>{accessDeniedDate}</strong>.
                      </>
                    )}{" "}
                    Kindly renew at the earliest.
                  </span>
                  <span className="payment-ticker-sep">•</span>
                </span>
              ))}
            </span>
          </div>
        </>
      )}
    </>
  );
}
